import { makeStyles } from 'tss-react/mui'
import { useTheme } from '@mui/material/styles'
import { useState, useRef, ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import {
  Badge,
  Popper,
  Paper,
  ClickAwayListener,
  Button,
  Fade,
  useMediaQuery,
  Typography,
} from '@mui/material'
import ResponsiveModal, {
  ResponsiveModalBody,
} from '@src/components/ResponsiveModal/ResponsiveModal'
import BellIcon from '@src/icons/BellIcon'
import { NotificationsResponse } from '@src/types/graphql-types'
import { sendEvent } from '@src/utils/analytics/coreAnalytics'
import { GTMEventTypes } from '@sial/common-utils'

const useStyles = makeStyles<void, 'arrow'>()((theme, _params, classes) => {
  return {
    trigger: {
      minWidth: 0,
      color: theme.palette.common.black,
      '&:hover, &:focus': {
        color: theme.palette.common.black,
      },
    },
    notificationBadge: {
      color: theme.palette.brand.main,
      '& .MuiBadge-badge': {
        fontSize: theme.typography.pxToRem(8),
        height: theme.spacing(3.5),
        minWidth: theme.spacing(3.5),
        width: theme.spacing(3.5),
      },
      '& .MuiBadge-anchorOriginTopRightCircular': {
        top: '4px',
        right: '2px',
      },
    },
    popper: {
      zIndex: 1300,
      [`&[x-placement*="bottom"] .${classes.arrow}`]: {
        top: 8,
        left: 0,
        marginTop: '-0.71em',
        marginLeft: 4,
        marginRight: 4,
        '&::before': {
          transformOrigin: '0 100%',
        },
      },
    },
    paper: {
      marginTop: theme.spacing(4),
      boxShadow: '0px 0px 5px 1px #00000040',
    },
    arrow: {
      position: 'absolute',
      overflow: 'hidden',
      width: '1.8em',
      height: '1.3em',
      boxSizing: 'border-box',
      color: theme.palette.background.grey,
      '&::before': {
        boxShadow: '0px -1px 2px 0px #00000040',
        content: '""',
        margin: 'auto',
        display: 'block',
        width: '100%',
        height: '100%',
        backgroundColor: theme.palette.background.grey,
        transform: 'rotate(45deg)',
      },
    },
    container: {
      maxHeight: '486px',
      overflowY: 'auto',
      minWidth: '345px',
      [theme.breakpoints.up('sm')]: {
        minWidth: '375px',
      },
    },
    header: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      padding: theme.spacing(4, 0, 4, 4),
      fontSize: theme.typography.pxToRem(20),
      fontWeight: theme.typography.fontWeightBold,
      backgroundColor: theme.palette.background.grey,
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
      borderRadius: '6px 6px 0px 0px',
    },
    modal: {
      backgroundColor: theme.palette.background.grey,
      paddingBottom: theme.spacing(4),
      alignItems: 'center',
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
    modalBody: {
      padding: 0,
    },
  }
})

export interface NotificationWrapperProps {
  notifications: NotificationsResponse[]
  children: ReactNode
}

const NotificationsWrapper: React.FC<NotificationWrapperProps> = ({
  notifications,
  children,
}) => {
  const { classes } = useStyles()
  const [open, setOpen] = useState(false)
  const [arrowRef, setArrowRef] = useState<HTMLElement | null>(null)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  const handleOpen = () => {
    setOpen(true)

    sendEvent({
      eventType: GTMEventTypes.AnalyticsEvent,
      payload: {
        event: 'notification_interaction',
        action: 'view notification',
        detail: undefined,
        section: undefined,
        component: 'NotificationsWrapper',
        element_type: 'icon',
        element_text: undefined,
        link_url: undefined,
        core_event: 'no',
        user_detail: undefined,
        event_group: undefined,
        event_subgroup: undefined,
      },
    })
  }

  const handleClose = (event: Event) => {
    if (!isDesktop) {
      return setOpen(false)
    }

    if (
      anchorRef.current &&
      anchorRef.current?.contains(event.target as HTMLElement)
    ) {
      return
    }

    setOpen(false)
  }

  return (
    <>
      <Button
        ref={anchorRef}
        onClick={handleOpen}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        className={classes.trigger}
      >
        <Badge
          badgeContent={notifications?.length}
          color="error"
          invisible={notifications?.length === 0}
          className={classes.notificationBadge}
          overlap="circular"
        >
          <BellIcon />
        </Badge>
      </Button>

      {!isDesktop && (
        <ResponsiveModal
          open={open}
          onClose={handleClose}
          customHeaderStyle={classes.modal}
          renderTitle={() => (
            <Typography variant="h2" component="h2">
              <FormattedMessage
                id="NOTIFICATIONS"
                defaultMessage="NOTIFICATIONS"
              />
            </Typography>
          )}
        >
          <Fade in={open}>
            <div>
              <ResponsiveModalBody className={classes.modalBody}>
                <div>{children}</div>
              </ResponsiveModalBody>
            </div>
          </Fade>
        </ResponsiveModal>
      )}

      {isDesktop && (
        <Popper
          className={classes.popper}
          placement="bottom"
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          modifiers={[
            {
              name: 'arrow',
              enabled: true,
              options: {
                element: arrowRef,
              },
            },
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                boundary: 'window',
              },
            },
          ]}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={250}>
              <Paper className={classes.paper}>
                <span className={classes.arrow} ref={setArrowRef} />
                <ClickAwayListener onClickAway={handleClose}>
                  <div>
                    <div className={classes.container}>
                      <h3 className={classes.header}>
                        <FormattedMessage
                          id="NOTIFICATIONS"
                          defaultMessage="NOTIFICATIONS"
                        />
                      </h3>
                      {children}
                    </div>
                  </div>
                </ClickAwayListener>
              </Paper>
            </Fade>
          )}
        </Popper>
      )}
    </>
  )
}

export default NotificationsWrapper
